import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as FillImageIcon } from '../../../assets/images/icon-fill-image.svg';

import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import usePopover from '../../../hooks/usePopover';
import { getActionPrefix } from '../../../utils/helpers';

interface IProps {
  value: number;
  onChange: (value: number) => void;
  dataAction?: string;
}

export const ZoomController = ({ value, onChange, dataAction }: IProps) => {
  const theme = useTheme();
  const { anchorEl, isOpen, handleOpen, handleClose } = usePopover();
  const [localValue, setLocalValue] = useState<number>(value);

  const dataActionPrefix = useMemo(() => getActionPrefix(dataAction), [dataAction]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const debouncedOnChange = useDebouncedCallback((newValue: number) => {
    onChange(newValue);
  }, 1000);

  const handleZoomIn = useCallback(() => {
    onChange(value + 10);
  }, [value, onChange]);

  const handleZoomOut = useCallback(() => {
    if (value <= 10) {
      return;
    }
    onChange(value - 10);
  }, [value, onChange]);

  const handleZoomToFit = useCallback(() => {
    onChange(100);
  }, [onChange]);

  const handleZoomTo = useCallback(
    (percentage: number) => () => {
      onChange(percentage);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(event.target.value);
      setLocalValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange],
  );

  const sizeSettingsConfig = useMemo(
    () => ({
      group1: [
        { label: 'Zoom in', icon: <ZoomInIcon />, fn: handleZoomIn, dataAction: `${dataActionPrefix}zoom_in_btn` },
        { label: 'Zoom out', icon: <ZoomOutIcon />, fn: handleZoomOut, dataAction: `${dataActionPrefix}zoom_out_btn` },
        {
          label: 'Zoom to fit',
          icon: <FillImageIcon />,
          fn: handleZoomToFit,
          dataAction: `${dataActionPrefix}zoom_to_fit_btn`,
        },
      ],
      group2: [
        { label: 'Zoom to 50%', icon: null, fn: handleZoomTo(50), dataAction: `${dataActionPrefix}zoom_to_50_btn` },
        { label: 'Zoom to 100%', icon: null, fn: handleZoomTo(100), dataAction: `${dataActionPrefix}zoom_to_100_btn` },
        { label: 'Zoom to 200%', icon: null, fn: handleZoomTo(200), dataAction: `${dataActionPrefix}zoom_to_200_btn` },
      ],
    }),
    [handleZoomIn, handleZoomOut, handleZoomToFit, handleZoomTo],
  );

  const renderListItems = useCallback(
    (items: { label: string; icon: React.ReactNode; fn: () => void; dataAction: string }[]) => {
      return items.map(({ label, icon = null, fn }) => (
        <ListItem key={label} disablePadding>
          <ListItemButton
            onClick={fn}
            data-action={dataActionPrefix}
            sx={{
              py: 0.5,
              gap: 1,
            }}
          >
            {icon && (
              <ListItemIcon
                sx={{
                  width: 20,
                  height: 20,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {icon}
              </ListItemIcon>
            )}
            <ListItemText primary={label} />
          </ListItemButton>
        </ListItem>
      ));
    },
    [dataActionPrefix],
  );

  return (
    <Box>
      <Button
        variant="text"
        size="small"
        endIcon={<ExpandMoreIcon sx={{ color: `${theme.palette.action.active}` }} />}
        onClick={handleOpen}
        data-action={`${dataActionPrefix}zoom_controller_btn`}
      >
        <Typography variant="body2" sx={{ width: 40, textAlign: 'end', color: `${theme.palette.action.active}` }}>
          {value}%
        </Typography>
      </Button>
      <Popover
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <TextField
          sx={{ m: 2 }}
          size="small"
          value={localValue}
          onChange={handleChange}
          aria-label="Zoom percentage"
          data-action={`${dataActionPrefix}zoom_controller_input_field`}
        />
        <Divider />
        <List>
          {renderListItems(sizeSettingsConfig.group1)}
          <Divider />
          {renderListItems(sizeSettingsConfig.group2)}
        </List>
      </Popover>
    </Box>
  );
};
